import type { ErrorLike } from '@apollo/client';
import { useQuery, skipToken } from "@apollo/client/react";
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { VERIFY_TOKEN } from '../graphql/resolvers/queries';
import { useSearchParams } from 'react-router-dom';

// Define the context type
interface MagicLinkContextType {
    token: string | null;
    tokenValid: boolean;
    tokenData: any;
    loading: boolean;
    error: ErrorLike | null
}

// Create the context with undefined initial value
const MagicLinkContext = createContext<MagicLinkContextType | undefined>(undefined);

interface MagicLinkProviderProps {
    children: ReactNode;
}

export function MagicLinkProvider({ children }: MagicLinkProviderProps) {
    // Get token from URL
    const [searchParams] = useSearchParams();
    const [token] = useState<string | null>(searchParams.get('token'));
    const [tokenValid, setTokenValid] = useState<boolean>(false);
    const [tokenData, setTokenData] = useState<any>(null);
    const [error, setError] = useState<ErrorLike|null>(null)

    // Verify token
    const { data: rawData, error: queryError, loading } = useQuery(VERIFY_TOKEN, (!token) ? skipToken : { fetchPolicy: 'network-only', variables: { token } });

    useEffect(() => {
        if (rawData) {
            const { valid, data } = rawData.verifyToken;
            setTokenValid(valid);
            setTokenData(JSON.parse(data));
        }
    }, [rawData]);

    useEffect(() => {
        if (queryError) {
            setTokenValid(false);
            setError(queryError);
        }
    }, [queryError]);

    return (
        <MagicLinkContext.Provider value={{ token, tokenValid, tokenData, loading, error }}>
            {!loading && children}
        </MagicLinkContext.Provider>
    );
}

export function useMagicLink() {
    const context = useContext(MagicLinkContext);
    if (context === undefined) {
        throw new Error('useMagicLink must be used within a MagicLinkProvider');
    }
    return context;
}
